/*
  Text popup for the "Image with text" section (sections/section-double.liquid).

  One <text-popup> element is rendered per section instance and wraps a <dialog>.
  Anything inside the same section opens it:
    - <a data-text-popup-open>   the Text block's "Popup link text"
    - <a href="#popup">          links added in the rich text editor
    - <button data-text-popup-open>  the "Popup button" block

  Uses the theme's scroll lock (theme:scroll:lock / theme:scroll:unlock events)
  and focus trap (window.theme.a11y) when they are available.
*/
(function () {
  if (customElements.get('text-popup')) return;

  const SELECTORS = {
    dialog: 'dialog',
    close: '[data-text-popup-close]',
    scrollable: '[data-scroll-lock-scrollable]',
    triggers: '[data-text-popup-open], a[href="#popup"]',
    section: '[data-section-type]',
  };

  const CLOSE_ANIMATION_FALLBACK_MS = 400;

  class TextPopup extends HTMLElement {
    constructor() {
      super();
      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onCloseClick = this.onCloseClick.bind(this);
      this.onDialogClick = this.onDialogClick.bind(this);
      this.onCancel = this.onCancel.bind(this);
      this.onClosed = this.onClosed.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);
    }

    connectedCallback() {
      this.dialog = this.querySelector(SELECTORS.dialog);
      if (!this.dialog) return;

      this.scrollable = this.dialog.querySelector(SELECTORS.scrollable);
      this.section = this.closest(SELECTORS.section) || this.parentElement;
      this.triggers = Array.from(this.section.querySelectorAll(SELECTORS.triggers)).filter((el) => !this.dialog.contains(el));
      this.lastTrigger = null;
      this.isClosing = false;

      this.triggers.forEach((el) => el.addEventListener('click', this.onTriggerClick));
      this.dialog.querySelectorAll(SELECTORS.close).forEach((el) => el.addEventListener('click', this.onCloseClick));
      this.dialog.addEventListener('click', this.onDialogClick);
      this.dialog.addEventListener('cancel', this.onCancel);
      this.dialog.addEventListener('close', this.onClosed);

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      }
    }

    disconnectedCallback() {
      if (!this.dialog) return;

      this.triggers.forEach((el) => el.removeEventListener('click', this.onTriggerClick));
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);

      // The theme editor can remove the section while the popup is open.
      if (this.dialog.hasAttribute('open')) this.releasePage();
    }

    /* ---- Event handlers ---- */

    onTriggerClick(event) {
      event.preventDefault();
      this.lastTrigger = event.currentTarget;
      this.open();
    }

    onCloseClick(event) {
      event.preventDefault();
      this.close();
    }

    // A click whose target is the <dialog> itself landed on the backdrop.
    onDialogClick(event) {
      if (event.target === this.dialog) this.close();
    }

    // Escape key. Prevent the instant native close so the exit animation runs.
    onCancel(event) {
      event.preventDefault();
      this.close();
    }

    // Theme editor: show the popup while one of its blocks is selected.
    onBlockSelect(event) {
      if (this.dialog.contains(event.target)) this.open();
    }

    onBlockDeselect(event) {
      if (this.dialog.contains(event.target)) this.close();
    }

    /* ---- Open / close ---- */

    open() {
      if (this.dialog.open || this.isClosing) return;

      this.dialog.removeAttribute('inert');
      this.dialog.setAttribute('aria-hidden', 'false');

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }

      if (this.scrollable) this.scrollable.scrollTop = 0;

      document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true, detail: this.scrollable}));

      const a11y = window.theme && window.theme.a11y;
      if (a11y && typeof a11y.trapFocus === 'function') {
        a11y.trapFocus(this.dialog, {elementToFocus: this.dialog});
      } else {
        this.dialog.focus();
      }
    }

    close() {
      if (!this.dialog.open || this.isClosing) return;

      this.isClosing = true;
      this.dialog.setAttribute('closing', '');

      let finished = false;
      let fallbackTimer = 0;

      const finish = () => {
        if (finished) return;
        finished = true;
        clearTimeout(fallbackTimer);
        this.dialog.removeEventListener('animationend', onAnimationEnd);

        if (typeof this.dialog.close === 'function') {
          this.dialog.close(); // fires the 'close' event -> onClosed()
        } else {
          this.dialog.removeAttribute('open');
          this.onClosed();
        }

        this.dialog.removeAttribute('closing');
      };

      const onAnimationEnd = (event) => {
        if (event.target === this.dialog) finish();
      };

      this.dialog.addEventListener('animationend', onAnimationEnd);
      fallbackTimer = setTimeout(finish, CLOSE_ANIMATION_FALLBACK_MS);
    }

    onClosed() {
      this.isClosing = false;
      this.dialog.removeAttribute('closing');
      this.dialog.setAttribute('inert', '');
      this.dialog.setAttribute('aria-hidden', 'true');
      this.releasePage();

      if (this.lastTrigger && typeof this.lastTrigger.focus === 'function') {
        this.lastTrigger.focus({preventScroll: true});
      }
    }

    // Give scrolling and focus back to the page, unless another modal is still open.
    releasePage() {
      const a11y = window.theme && window.theme.a11y;
      if (a11y && typeof a11y.removeTrapFocus === 'function') a11y.removeTrapFocus();

      const othersOpen = window.theme && typeof window.theme.hasOpenModals === 'function' ? window.theme.hasOpenModals() : false;
      if (!othersOpen) {
        document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
      }
    }
  }

  customElements.define('text-popup', TextPopup);
})();
